import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
const AccountOverviewContext = createContext(undefined);
export const ACCOUNT_OVERVIEW_SESSION_KEY = 'account_overview_context';
const SESSION_STORAGE_KEY = ACCOUNT_OVERVIEW_SESSION_KEY;
export const AccountOverviewProvider = ({ children }) => {
    const token = useSelector((state) => state.auth.token);
    // Initialize from sessionStorage
    const [state, setState] = useState(() => {
        const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (saved) {
            try {
                return JSON.parse(saved);
            }
            catch (e) {
                console.error("Failed to parse account overview context from session storage", e);
            }
        }
        return {
            selectedClientId: '',
            selectedMonth: '',
            dateRange: { start: null, end: null },
            compareDateRange: null,
            isCompareOn: false,
            clientType: 'View All',
            totalGoalToggle: false,
            isArchiveMode: false,
            activeTab: 'account_overview',
        };
    });
    // Reset state on logout
    useEffect(() => {
        if (!token) {
            setState({
                selectedClientId: '',
                selectedMonth: '',
                dateRange: { start: null, end: null },
                compareDateRange: null,
                isCompareOn: false,
                clientType: 'View All',
                totalGoalToggle: false,
                isArchiveMode: false,
                activeTab: 'account_overview',
            });
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
    }, [token]);
    // Sync with sessionStorage
    useEffect(() => {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
    }, [state]);
    const setClientId = useCallback((id) => {
        setState((prev) => ({ ...prev, selectedClientId: id }));
    }, []);
    const setMonth = useCallback((month) => {
        setState((prev) => ({ ...prev, selectedMonth: month }));
    }, []);
    const setDateRange = useCallback((range) => {
        setState((prev) => ({ ...prev, dateRange: range }));
    }, []);
    const setCompareDateRange = useCallback((range) => {
        setState((prev) => ({ ...prev, compareDateRange: range }));
    }, []);
    const setIsCompareOn = useCallback((on) => {
        setState((prev) => ({ ...prev, isCompareOn: on }));
    }, []);
    const setClientType = useCallback((type) => {
        setState((prev) => ({ ...prev, clientType: type }));
    }, []);
    const setTotalGoalToggle = useCallback((on) => {
        setState((prev) => ({ ...prev, totalGoalToggle: on }));
    }, []);
    const setIsArchiveMode = useCallback((on) => {
        setState((prev) => ({ ...prev, isArchiveMode: on }));
    }, []);
    const setActiveTab = useCallback((tab) => {
        setState((prev) => ({ ...prev, activeTab: tab }));
    }, []);
    const setContextState = useCallback((newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    }, []);
    const value = {
        ...state,
        setClientId,
        setMonth,
        setDateRange,
        setCompareDateRange,
        setIsCompareOn,
        setClientType,
        setTotalGoalToggle,
        setIsArchiveMode,
        setActiveTab,
        setContextState,
    };
    return (_jsx(AccountOverviewContext.Provider, { value: value, children: children }));
};
export const useAccountOverview = () => {
    const context = useContext(AccountOverviewContext);
    if (context === undefined) {
        throw new Error('useAccountOverview must be used within an AccountOverviewProvider');
    }
    return context;
};
